import React from "react";
import {
  AbsoluteFill,
  interpolate,
  random,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

export interface SpeedLinesProps {
  color?: string;
  from?: number;
  durationInFrames?: number;
  lineCount?: number;
  opacity?: number;
  innerRadiusRatio?: number; // Center clear hole ratio
}

/**
 * Authentic Speed Lines Element from Remotion Elements (storytelling/speed-lines)
 * Generates dynamic animated radial anime/action speed lines with randomized spread per 2 frames.
 */
export const SpeedLines: React.FC<SpeedLinesProps> = ({
  color = "#5D3FD3", // Kyu Purple or White or Gold
  from = 0,
  durationInFrames,
  lineCount = 56,
  opacity = 0.85,
  innerRadiusRatio = 0.38,
}) => {
  const frame = useCurrentFrame();
  const config = useVideoConfig();
  const relFrame = frame - from;
  const dur = durationInFrames ?? config.durationInFrames;

  if (relFrame < 0 || relFrame >= dur) return null;

  const { width, height } = config;
  const seed = Math.floor(relFrame / 2);
  const cx = width / 2;
  const cy = height / 2;
  const far = Math.hypot(width, height);

  const lines = new Array(lineCount).fill(true).map((_, i) => {
    const angle = (i / lineCount) * Math.PI * 2 + random(`angle-${i}-${seed}`) * 0.08;
    const spread = 0.006 + random(`spread-${i}-${seed}`) * 0.014;
    const inner = innerRadiusRatio + random(`inner-${i}-${seed}`) * 0.22;
    const x1 = cx + Math.cos(angle - spread) * far;
    const y1 = cy + Math.sin(angle - spread) * far;
    const x2 = cx + Math.cos(angle + spread) * far;
    const y2 = cy + Math.sin(angle + spread) * far;
    const xi = cx + Math.cos(angle) * inner * (width / 2);
    const yi = cy + Math.sin(angle) * inner * (height / 2);
    return `M ${xi} ${yi} L ${x1} ${y1} L ${x2} ${y2} Z`;
  });

  const fadeInOut = interpolate(
    relFrame,
    [0, 4, dur - 6, dur - 1],
    [0, opacity, opacity, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  return (
    <AbsoluteFill style={{ opacity: fadeInOut, pointerEvents: "none", zIndex: 40 }}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {lines.map((d, i) => (
          <path key={i} d={d} fill={color} />
        ))}
      </svg>
    </AbsoluteFill>
  );
};
